//------------------------------------------------
// Field formatter
// This will convert the text field to an option field
// For example: if the field is a dropdown, it will convert the text to an option
// This is useful for the CMS field editors
// Example: if the field offers options,
// the text value "Option" will be converted to an option object like
//  { value: 'option', name: 'Option' }
// When exporting the data, the value will be converted back to the text
//  'Option'
//------------------------------------------------

import { isInternalType } from "../internal-type"

export default {
  //fields inside instances of ContentType
  type: "Object",
  preFormat: (fieldValues, Type) => {
    // Don't change fields inside internally used types
    if (isInternalType(Type)) return fieldValues
    //No values, return
    if (!fieldValues) return fieldValues
    // If Type is not provided or has no fields, return as-is
    if (!Type || !Type.fields) return fieldValues

    const fieldTypes = Type.fields

    for (const field of fieldTypes) {
      if (!field) continue

      // Handle both string type and object type {value: "Object", name: "Object"}
      const fieldType = typeof field.type === "object" && field.type?.value
        ? field.type.value
        : field.type

      if (fieldType !== "Object") continue

      const value = fieldValues[field.id]
      if (!value || typeof value !== "object" || Array.isArray(value)) continue

      // Convert object to array of key-value pairs
      fieldValues[field.id] = Object.entries(value).map(([key, val]) => {
        return {
          key,
          value: val,
        }
      })
    }

    return fieldValues
  },
  postFormat: (fieldValues, Type) => {
    // Don't change fields inside internally used types
    if (isInternalType(Type)) return fieldValues
    // If Type is not provided or has no fields, return as-is
    if (!Type || !Type.fields) return fieldValues
    //implement postformat
    const fieldTypes = Type.fields

    for (const field of fieldTypes) {
      if (!field) continue

      // Handle both string type and object type {value: "Object", name: "Object"}
      const fieldType = typeof field.type === "object" && field.type?.value
        ? field.type.value
        : field.type

      //skip non objects
      if (fieldType !== "Object") continue
      if (!fieldValues[field.id]) continue

      //example: [ { key: 1, value: 'value1' }, { key: 2, value: 'value2' } ]
      //convert to { 1: 'value1', 2: 'value2' }
      if (
        Array.isArray(fieldValues[field.id]) &&
        fieldValues[field.id].length > 0 &&
        fieldValues[field.id].every((item) => {
          return item.key && typeof item.key === "string"
        })
      ) {
        const toObject = fieldValues[field.id].reduce((acc, item) => {
          let value = item.value ?? ""

          // If the value is an option object (has both value and name properties),
          // extract just the value part
          if (value && typeof value === "object" && !Array.isArray(value) && value.value !== undefined) {
            value = value.value
          }

          acc[item.key] = value
          return acc
        }, {})

        fieldValues[field.id] = toObject
      }
    }

    return fieldValues
  },
  postFormatValue: (value) => {
    //if value is an array of object in the form of { key: 'key', value: 'value' }
    //convert it to an array of strings where:
    // [ "{{key}}: value" ]
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      //every item has a key field (value may be empty string)
      value.every((item) => {
        return item.key
      })
    )
      return value.map((item) => `{{${item.key}}}: ${item.value ?? ""}`)

    //return the original value
    return value
  },
  //implement preformat value
  preFormatValue: (value) => {
    //if value is an array of strings in the form of "{{key}}: value"
    //convert it to an array of objects where:
    // [ { key: 'key', value: 'value' } ]
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      //every item is a string
      value.every((item) => typeof item === "string")
    ) {
      return value.map((item) => {
        const colonIndex = item.indexOf(': ')
        if (colonIndex === -1) {
          // Fallback if no ': ' found
          return { key: item.replace(/{{|}}/g, "").trim(), value: "" }
        }
        const key = item.substring(0, colonIndex).replace(/{{|}}/g, "").trim()
        const value = item.substring(colonIndex + 2).trim() // +2 to skip ': '
        return { key, value }
      })
    }

    // Handle array of objects (already parsed format) — strip any stray {{}} from keys
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      value.every((item) => typeof item === "object" && item !== null && "key" in item)
    ) {
      return value.map((item) => ({
        ...item,
        key: (item.key || "").replace(/\{\{|\}\}/g, "").trim(),
      }))
    }

    //return the original value
    return value
  },
}
